/**
 * §3 Task 8 — light BOTH branches on fork-land, so the operator doesn't lose the
 * trunk when a new path is born. Two pure pieces (unit-tested next door):
 *   - forkLandPair: from the message graph + the in-flight pending set, resolve the
 *     latest fork's {forkRootId (FR), trunkContinuationId (M's non-FR child)}.
 *   - decorateForkLand: add `chatmap-fork-split` to both — identity-preserving
 *     (returns the SAME array ref when there's nothing to light, so ReactFlow's
 *     fast-path survives).
 */
interface ForkMsg {
  id: string;
  parent_ids?: string[];
  metadata?: { is_spine_root?: boolean; fork_id?: string; fork_origin_id?: string };
}

export interface ForkLandPair {
  forkRootId: string | null;
  trunkContinuationId: string | null;
}

export function forkLandPair(messages: ForkMsg[], pendingSpineIds: Set<string>): ForkLandPair {
  // Latest in-flight fork = a pending spine-root marker stamped with fork_id.
  let fr: ForkMsg | null = null;
  for (const m of messages) {
    const md = m.metadata || {};
    if (md.is_spine_root && md.fork_id && pendingSpineIds.has(m.id)) fr = m; // document order → last wins
  }
  if (!fr) return { forkRootId: null, trunkContinuationId: null };
  const M = (fr.metadata || {}).fork_origin_id;
  if (!M) return { forkRootId: fr.id, trunkContinuationId: null };
  // Trunk continuation = M's child that ISN'T the fork (the original next turn).
  const trunk = messages.find((m) => (m.parent_ids || [])[0] === M && m.id !== fr!.id);
  return { forkRootId: fr.id, trunkContinuationId: trunk ? trunk.id : null };
}

export function decorateForkLand<T extends { id: string; className?: string }>(
  nodes: T[],
  pair: ForkLandPair,
): T[] {
  if (!pair.forkRootId && !pair.trunkContinuationId) return nodes; // identity fast-path
  return nodes.map((n) =>
    n.id === pair.forkRootId || n.id === pair.trunkContinuationId
      ? { ...n, className: `${n.className ?? ''} chatmap-fork-split`.trim() }
      : n);
}
